import { useState, useEffect, useRef } from "react"
import { FlaskConical, Play, TrendingUp, TrendingDown, Download, ChevronRight, CheckCircle, XCircle, BarChart2 } from "lucide-react"
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell, ReferenceLine } from "recharts"
import api from "../services/api"
import { STRATEGY_TYPES, TIMEFRAMES, POPULAR_PAIRS } from "../utils/constants"
import { formatCurrency, formatPct, formatDate, statusColor } from "../utils/formatters"
import Badge from "../components/common/Badge"
import { PageLoader } from "../components/common/LoadingSpinner"
import toast from "react-hot-toast"

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]

function MonthlyHeatmap({ trades }) {
  if (!trades?.length) return null
  const monthly = {}
  trades.forEach(t => {
    if (!t.closed_at) return
    const d = new Date(t.closed_at)
    const key = `${d.getFullYear()}-${d.getMonth()}`
    monthly[key] = (monthly[key] || 0) + (t.pnl_pct || 0)
  })
  const year = new Date().getFullYear()
  return (
    <div>
      <p className="text-sm font-medium text-gray-400 mb-2">Monthly Returns</p>
      <div className="grid grid-cols-6 gap-1.5">
        {MONTHS.map((m, i) => {
          const val = monthly[`${year}-${i}`] ?? null
          const bg = val === null ? "bg-surface-100" : val > 0 ? "bg-trading-green/20 border-trading-green/30" : "bg-trading-red/20 border-trading-red/30"
          return (
            <div key={m} className={`${bg} border border-surface-200 rounded-lg p-2 text-center`}>
              <p className="text-[10px] text-gray-500">{m}</p>
              <p className={`text-xs font-mono font-bold ${val === null ? "text-gray-600" : val > 0 ? "text-trading-green" : "text-trading-red"}`}>
                {val === null ? "—" : `${val > 0 ? "+" : ""}${val.toFixed(1)}%`}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ResultsPanel({ bt }) {
  if (!bt || bt.status !== "completed") return null
  const metrics = [
    ["Total Return", formatPct(bt.total_return_pct), bt.total_return_pct >= 0 ? "text-trading-green" : "text-trading-red"],
    ["Sharpe Ratio", bt.sharpe_ratio?.toFixed(2) ?? "—", "text-white"],
    ["Max Drawdown", formatPct(-(bt.max_drawdown_pct ?? 0)), "text-trading-red"],
    ["Win Rate", formatPct(bt.win_rate_pct), "text-white"],
    ["Total Trades", bt.total_trades, "text-white"],
    ["Profit Factor", bt.profit_factor?.toFixed(2) ?? "—", (bt.profit_factor ?? 0) >= 1 ? "text-trading-green" : "text-trading-red"],
    ["Avg Win", formatPct(bt.avg_win_pct), "text-trading-green"],
    ["Avg Loss", formatPct(bt.avg_loss_pct), "text-trading-red"],
  ]
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white">{bt.name} — Results</h3>
        <div className="flex items-center gap-2">
          <Badge variant="blue">{bt.symbol}</Badge>
          <Badge variant="gray">{bt.timeframe}</Badge>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {metrics.map(([k, v, c]) => (
          <div key={k} className="bg-surface-100 rounded-xl p-3 text-center">
            <p className="text-[10px] text-gray-400 mb-1">{k}</p>
            <p className={`font-mono font-bold text-sm ${c}`}>{v}</p>
          </div>
        ))}
      </div>
      {bt.equity_curve?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-400 mb-2">Equity Curve</p>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={bt.equity_curve}>
              <defs>
                <linearGradient id="btGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a"/>
              <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v?.slice(5,10)}/>
              <YAxis tick={{ fill: "#6b7280", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v/1000).toFixed(1)}k`}/>
              <Tooltip contentStyle={{ background: "#1a1d27", border: "1px solid #2a2d3a", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#9ca3af" }}/>
              <Area type="monotone" dataKey="equity" stroke="#6366f1" strokeWidth={2} fill="url(#btGrad)"/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <MonthlyHeatmap trades={bt.trades}/>
      {bt.trades?.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-400 mb-2">Last 10 Trades</p>
          <div className="overflow-x-auto rounded-xl">
            <table className="w-full text-xs min-w-[500px]">
              <thead><tr className="text-gray-500 border-b border-surface-200">
                {["Symbol","Side","Entry","Exit","P&L","Opened"].map(h => <th key={h} className="pb-2 text-left px-2 font-medium">{h}</th>)}
              </tr></thead>
              <tbody>{bt.trades.slice(-10).reverse().map((t,i) => (
                <tr key={i} className="border-b border-surface-200/50 hover:bg-surface-100">
                  <td className="py-1.5 px-2 font-mono text-white">{t.symbol}</td>
                  <td className="py-1.5 px-2"><span className={t.side === "buy" ? "text-trading-green" : "text-trading-red"}>{t.side?.toUpperCase()}</span></td>
                  <td className="py-1.5 px-2 font-mono text-gray-300">{formatCurrency(t.entry_price)}</td>
                  <td className="py-1.5 px-2 font-mono text-gray-400">{t.exit_price ? formatCurrency(t.exit_price) : "—"}</td>
                  <td className={`py-1.5 px-2 font-mono font-semibold ${(t.pnl_pct ?? 0) >= 0 ? "text-trading-green" : "text-trading-red"}`}>{formatPct(t.pnl_pct)}</td>
                  <td className="py-1.5 px-2 text-gray-500">{formatDate(t.opened_at)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

export default function Backtesting() {
  const [backtests, setBacktests] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [running, setRunning] = useState(false)
  const [form, setForm] = useState({ name: "EMA BTC 30d", symbol: "BTC/USDT", timeframe: "1h", strategy_type: "ema_crossover", start_date: "2024-01-01", end_date: "2024-06-01", initial_capital: 10000, parameters: {} })
  const pollRef = useRef(null)

  async function load() {
    try {
      const { data } = await api.get("/backtesting/")
      setBacktests(data)
    } catch (err) {
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        toast.error("Server is slow to respond. Try refreshing in a moment.")
      }
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [])

  async function runBacktest(e) {
    e.preventDefault()
    setRunning(true)
    try {
      const { data } = await api.post("/backtesting/", { ...form, start_date: new Date(form.start_date).toISOString(), end_date: new Date(form.end_date).toISOString(), initial_capital: parseFloat(form.initial_capital) })
      toast.success("Backtest started!")
      await load()
      let attempts = 0
      if (pollRef.current) clearInterval(pollRef.current)
      pollRef.current = setInterval(async () => {
        attempts++
        try {
          const { data: bt } = await api.get(`/backtesting/${data.id}`)
          if (bt.status === "completed" || bt.status === "failed" || attempts > 60) {
            clearInterval(pollRef.current)
            pollRef.current = null
            await load()
            if (bt.status === "completed") { setSelected(bt); toast.success("Backtest complete!") }
            else if (bt.status === "failed") toast.error("Backtest failed")
          }
        } catch { clearInterval(pollRef.current); pollRef.current = null }
      }, 3000)
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to run backtest") }
    finally { setRunning(false) }
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Form */}
      <div className="xl:col-span-1 space-y-4">
        <div className="card">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2"><FlaskConical size={18} className="text-brand-400"/>New Backtest</h3>
          <form onSubmit={runBacktest} className="space-y-3">
            <div><label className="label">Name</label><input className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required/></div>
            <div>
              <label className="label">Strategy</label>
              <select className="input" value={form.strategy_type} onChange={e => setForm({...form, strategy_type: e.target.value})}>
                {STRATEGY_TYPES.filter(s => s.value !== "custom").map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Symbol</label>
              <select className="input" value={form.symbol} onChange={e => setForm({...form, symbol: e.target.value})}>
                {POPULAR_PAIRS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Timeframe</label>
              <select className="input" value={form.timeframe} onChange={e => setForm({...form, timeframe: e.target.value})}>
                {TIMEFRAMES.map(tf => <option key={tf.value} value={tf.value}>{tf.label}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="label">Start Date</label><input type="date" className="input" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}/></div>
              <div><label className="label">End Date</label><input type="date" className="input" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}/></div>
            </div>
            <div><label className="label">Initial Capital (USD)</label><input type="number" className="input" value={form.initial_capital} onChange={e => setForm({...form, initial_capital: e.target.value})} min="100"/></div>
            <button type="submit" disabled={running} className="btn-primary w-full flex items-center justify-center gap-2">
              <Play size={15}/>{running ? "Running..." : "Run Backtest"}
            </button>
          </form>
        </div>
        {/* History */}
        <div className="card">
          <h3 className="font-semibold text-white mb-3">History ({backtests.length})</h3>
          {loading ? <div className="h-16 bg-surface-100 rounded animate-pulse"/> : backtests.length === 0 ? (
            <p className="text-gray-500 text-xs text-center py-4">No backtests yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {backtests.map(bt => (
                <button key={bt.id} onClick={() => bt.status === "completed" && setSelected(bt)} className="w-full text-left flex items-center justify-between p-2.5 bg-surface-100 hover:bg-surface-200 rounded-lg transition-colors">
                  <div>
                    <p className="font-medium text-white text-xs">{bt.name}</p>
                    <p className="text-[10px] text-gray-500">{bt.symbol} · {bt.timeframe} · {bt.strategy_type}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {bt.status === "completed" && <span className={`font-mono text-xs font-semibold ${(bt.total_return_pct ?? 0) >= 0 ? "text-trading-green" : "text-trading-red"}`}>{formatPct(bt.total_return_pct)}</span>}
                    <Badge variant={bt.status === "completed" ? "blue" : bt.status === "failed" ? "red" : "yellow"}>{bt.status}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Results */}
      <div className="xl:col-span-2">
        {selected ? (
          <div className="card"><ResultsPanel bt={selected}/></div>
        ) : (
          <div className="card text-center py-20">
            <FlaskConical size={48} className="mx-auto text-gray-600 mb-3"/>
            <h3 className="font-semibold text-white">Run a backtest to see results</h3>
            <p className="text-gray-400 text-sm mt-1">Configure your strategy settings on the left and click Run</p>
          </div>
        )}
      </div>
    </div>
  )
}
